// "Cancelled users" inner card of the Passengers section: who cancelled, with their retained passengers.
import { Card, Space, Table, Tag, Typography } from 'antd';

import type { AdminBookingDetail, AdminBookingPassenger } from '../../api/types';
import { AccountDeletedTag } from '../../components/AccountDeletedTag';
import { dobColumn, expiryColumn, genderColumn, issuerColumn, nationalityColumn } from '../../components/passengerColumns';

const { Text } = Typography;

export function CancelledUsersCard({ booking }: { booking: AdminBookingDetail }) {
  return (
    <Card
      type="inner"
      size="small"
      title={<Text type="secondary">Cancelled users</Text>}
    >
      <Table
        rowKey="id"
        dataSource={booking.cancelledUsers}
        pagination={false}
        size="small"
        // Regression 29 Sep - a long unbroken value (an email, a passport
        // number) made this table wider than its card and the whole page
        // scrolled sideways; it now scrolls inside the card instead.
        scroll={{ x: 'max-content' }}
        columns={[
          {
            title: 'Name',
            key: 'name',
            render: (_, u) => (
              <Space>
                <Text>{u.name || '—'}</Text>
                <Tag color="red">Cancelled</Tag>
                <AccountDeletedTag at={u.accountDeletedAt} />
              </Space>
            ),
          },
          { title: 'Email', dataIndex: 'email', key: 'email' },
          { title: 'Phone', dataIndex: 'phone', key: 'phone' },
        ]}
        // SHAR-494 — full passenger detail for a cancelled user, in the
        // same columns as the active-passenger tables above. Users
        // cancelled before passengers were soft-removed have none, and
        // say so rather than showing an empty table.
        expandable={{
          expandedRowRender: (u) =>
            u.passengers && u.passengers.length ? (
              <Table<AdminBookingPassenger>
                rowKey="id"
                dataSource={u.passengers}
                pagination={false}
                size="small"
                // Regression 29 Sep - a long unbroken value (an email, a passport
                // number) made this table wider than its card and the whole page
                // scrolled sideways; it now scrolls inside the card instead.
                scroll={{ x: 'max-content' }}
                columns={[
                  { title: 'Name', dataIndex: 'fullName', key: 'fullName' },
                  dobColumn<AdminBookingPassenger>(),
                  genderColumn<AdminBookingPassenger>(),
                  nationalityColumn<AdminBookingPassenger>(),
                  {
                    title: 'Passport no.',
                    key: 'pp',
                    render: (_, r) =>
                      r.passportPending ? <Tag>To be provided</Tag> : r.passportNumber || '—',
                  },
                  issuerColumn<AdminBookingPassenger>(),
                  expiryColumn<AdminBookingPassenger>(),
                  {
                    title: 'Documents',
                    key: 'docs',
                    render: (_, r) => {
                      const links: { url: string; label: string }[] = [];
                      if (r.passportPhotoUrl)
                        links.push({ url: r.passportPhotoUrl, label: 'Passport' });
                      r.extraDocUrls.forEach((u2, i) =>
                        links.push({ url: u2, label: `Doc ${i + 1}` }),
                      );
                      if (!links.length) return <Text type="secondary">—</Text>;
                      return (
                        <Space wrap size={8}>
                          {links.map((l) => (
                            <a key={l.url} href={l.url} target="_blank" rel="noreferrer">
                              {l.label}
                            </a>
                          ))}
                        </Space>
                      );
                    },
                  },
                ]}
              />
            ) : (
              <Text type="secondary">
                No passenger details retained for this user (cancelled before passenger
                records were preserved).
              </Text>
            ),
        }}
      />
    </Card>
  );
}
